import { match } from "ts-pattern";
import {
  Product,
  MeetingPoint,
  ProductMedia,
  // PricingPackage,
  ItineraryStop,
  ProductBookingSettings,
  TourDetails,
  CancellationPolicy,
  PickupPoint,
  PickupPointCategory,
  MeetingPickUpSetting,
  PickupOptionTypeEnum,
  AdditionalInfoTypeEnum,
  AccessibilityInfo,
  HealthRestrictionInfo,
  ConfirmationType,
  BookingCutoffType,
  PriceUnit,
  GroupDefinitionEnum,
  ProductOption,
  ProductSeason,
  PricingRecord,
  CustomizationTypeEnum,
  ProductOptionCustomization,
  PricingTimeModel,
  DayOfWeekEnum,
  PricingType,
  AgeBand,
  PricingTierAgeBand,
  PricingTierGroup,
  AgeDefinition,
  AgeBandPricingTier,
  PricingPackage,
} from "./tour.models";
import {
  AgeBandPricingSimpleTierDto,
  CreatePricingRecordDto,
} from "./dto/pricing-record.dto";

export class ProductMapper {
  static mapApiProductToModel(apiProduct: any): Product {
    const product: Product = {
      id: apiProduct.id,
      operatorId: apiProduct.operatorId,
      productType: apiProduct.productType,
      productClassification: apiProduct.productClassification,
      title: apiProduct.title,
      subtitle: apiProduct.subtitle,
      briefDescription: apiProduct.briefDescription,
      description: apiProduct.description,
      whatToBring: apiProduct.whatToBring,
      goodToKnow: apiProduct.goodToKnow,
      status: apiProduct.status,
      cancellationPolicyId: apiProduct.cancellationPolicyId,
      language: apiProduct.language,
      isPrivate: apiProduct.isPrivate,
      isCustomizable: apiProduct.isCustomizable,
      isSkipTheLine: apiProduct.isSkipTheLine,
      contactPhone: apiProduct.contactPhone,
      resellerType: apiProduct.resellerType,
      primaryLocationId: apiProduct.primaryLocationId,
      externalId: apiProduct.externalId,
      createdAt: new Date(apiProduct.createdAt),
      updatedAt: new Date(apiProduct.updatedAt),
      lastFinishStage: apiProduct.completionStage,
      cancellationPolicy: apiProduct.cancellationPolicy
        ? ProductMapper.mapCancellationPolicy(apiProduct.cancellationPolicy)
        : undefined,
      categories: apiProduct.categories,
      media: apiProduct.media?.map((m: any) =>
        ProductMapper.mapProductMedia(m),
      ),
      features: apiProduct.features,
      itineraryStops: apiProduct.itineraryStops?.map((stop: any) =>
        ProductMapper.mapItineraryStop(stop),
      ),
      bookingSettings: apiProduct.bookingSettings
        ? ProductMapper.mapBookingSettings(
            apiProduct.bookingSettings,
            apiProduct.ageBandDefinitions || [],
          )
        : undefined,
      tourDetails: apiProduct.tourDetails
        ? ProductMapper.mapTourDetails(apiProduct.tourDetails)
        : undefined,
      activityDetails: apiProduct.activityDetails,
      difficultyLevel: apiProduct.tourDetails?.difficultyLevel,
      accessibilities: apiProduct.additionalInfo
        ?.filter((info) =>
          [
            AdditionalInfoTypeEnum.WHEELCHAIR_ACCESSIBLE,
            AdditionalInfoTypeEnum.STROLLER_ACCESSIBLE,
            AdditionalInfoTypeEnum.PETS_WELCOME,
            AdditionalInfoTypeEnum.PUBLIC_TRANSPORTATION_NEARBY,
            AdditionalInfoTypeEnum.INFANTS_MUST_SIT_ON_LAPS,
            AdditionalInfoTypeEnum.INFANT_SEATS_AVAILABLE,
            AdditionalInfoTypeEnum.FREE_TEXT,
          ].includes(info.infoType),
        )
        .map(
          (info) =>
            ({
              infoType: info.infoType,
              isApplicable: info.isApplicable,
              customText: info.customText,
              sortOrder: info.sortOrder,
              externalReference: info.externalReference,
            }) as AccessibilityInfo,
        ),
      healthRestrictions: apiProduct.additionalInfo
        ?.filter((info) =>
          [
            AdditionalInfoTypeEnum.NO_BACK_PROBLEMS,
            AdditionalInfoTypeEnum.NO_PREGNANT,
            AdditionalInfoTypeEnum.NO_HEART_PROBLEMS,
            AdditionalInfoTypeEnum.HEALTH_FREE_TEXT,
          ].includes(info.infoType),
        )
        .map(
          (info) =>
            ({
              infoType: info.infoType,
              isApplicable: info.isApplicable,
              customText: info.customText,
              sortOrder: info.sortOrder,
              externalReference: info.externalReference,
            }) as HealthRestrictionInfo,
        ),
      productOptions:
        apiProduct.productOptions?.map((option: any) =>
          ProductMapper.mapProductOption(option),
        ) || [],
      taxonomyMaps: apiProduct.taxonomyMaps || [],
    };

    if (
      apiProduct.pickupDetails ||
      apiProduct.meetingPoints ||
      apiProduct.serviceLocationMaps
    ) {
      product.meetingPickupSetting = ProductMapper.mapMeetingPickupSetting(
        apiProduct.pickupDetails,
        apiProduct.meetingPoints,
        apiProduct.serviceLocationMaps,
      );
    }

    return product;
    // serviceLocationMaps: apiProduct.serviceLocationMaps,
    // sessions: apiProduct.sessions,
    // reviews: apiProduct.reviews,
  }
  private static mapMeetingPoint(mp: any): MeetingPoint {
    const meetingPoint = mp.meetingPoint || mp;
    return {
      searchString: meetingPoint?.searchString || "",
      name: meetingPoint?.name || "",
      address: meetingPoint?.address || "",
      latitude: parseFloat(meetingPoint?.latitude || "0"),
      longitude: parseFloat(meetingPoint?.longitude || "0"),
      instructions: mp.instructions || "",
      providerReference: meetingPoint?.providerReference,
      provider: meetingPoint.provider,
      externalReference: mp.externalReference,
    };
  }

  private static mapPickupPoint(locationMap: any): PickupPoint {
    return {
      searchString: locationMap.location?.searchString || "",
      name: locationMap.location?.name || "",
      address: locationMap.location?.address || "",
      centre: {
        lat: parseFloat(locationMap.location?.latitude || "0"),
        long: parseFloat(locationMap.location?.longitude || "0"),
      },
      category: locationMap.location?.category as PickupPointCategory,
      providerReference: locationMap.location?.providerReference,
      provider: locationMap.location?.serviceProvider,
      externalReference: locationMap.location?.externalReference,
    };
  }

  private static mapMeetingPickupSetting(
    pickupDetails: any,
    meetingPoints: any[] = [],
    serviceLocationMaps: any[] = [],
  ): MeetingPickUpSetting {
    const pickupPoints = serviceLocationMaps
      .filter((loc) => loc.serviceType === "PICKUP")
      .map((loc) => this.mapPickupPoint(loc));

    const startPoints = meetingPoints
      .filter((mp) => mp.pointType === "start")
      .map((mp) => this.mapMeetingPoint(mp));

    const endPoints = meetingPoints
      .filter((mp) => mp.pointType === "end")
      .map((mp) => this.mapMeetingPoint(mp));

    return {
      meetingPoint: {
        endsAtStartPoint: pickupDetails?.endsAtStartPoint || true,
        startPoints,
        endPoints,
      },
      pickupOption: {
        type: pickupDetails?.pickupOptionType as PickupOptionTypeEnum,
        customPickupAllowed: pickupDetails?.customPickupAllowed || true,
        minutesBeforeDepartureTimeForPickup:
          pickupDetails?.minutesBeforeDepartureForPickup || 60,
      },
      pickupPoints: pickupPoints.length
        ? {
            points: pickupPoints,
          }
        : undefined,
    };
  }

  private static mapProductMedia(media: any): ProductMedia {
    return {
      ...media,
      createdAt: media.createdAt ? new Date(media.createdAt) : undefined,
      updatedAt: media.updatedAt ? new Date(media.updatedAt) : undefined,
    };
  }

  private static mapItineraryStop(stop: any): ItineraryStop {
    return {
      ...stop,
      createdAt: new Date(stop.createdAt),
      updatedAt: new Date(stop.updatedAt),
      location: this.mapMeetingPoint(stop.location),
    };
  }

  private static mapBookingSettings(
    settings: any,
    ageBandDefinitions: {
      ageBand: AgeBand;
      minAge: number;
      maxAge: number;
    }[],
  ): ProductBookingSettings {
    return {
      confirmationType: settings.confirmationType as ConfirmationType,
      bookingCutoffType: settings.bookingCutoffType as BookingCutoffType,
      defaultBookingCutoffHours: settings.defaultBookingCutoffHours,
      bookingCutoffFixedTime: settings.bookingCutoffFixedTime,
      bookingCutoffFixedTimeOnDay: settings.bookingCutoffFixedTimeOnDay,
      isAdultRequired: settings.isAdultRequired,
      priceUnit: settings.priceUnit as PriceUnit,
      priceUnitGroup: settings.groupDefinition as GroupDefinitionEnum,
      maxParticipants: settings.maxParticipants,
      bookingCutoffOverrides: settings.bookingCutoffOverrides,
      ageBandDefinitions: ageBandDefinitions.reduce(
        (acc, aBD) => ({
          ...acc,
          [aBD.ageBand]: {
            minAge: aBD.minAge,
            maxAge: aBD.maxAge,
          },
        }),
        {
          [AgeBand.ADULT]: {
            minAge: 15,
            maxAge: 40,
          },
        } as AgeDefinition,
      ),
    };
  }

  private static mapTourDetails(details: any): TourDetails {
    return {
      ...details,
      createdAt: details.createdAt ? new Date(details.createdAt) : undefined,
      updatedAt: details.updatedAt ? new Date(details.updatedAt) : undefined,
    };
  }

  private static mapCancellationPolicy(policy: any): CancellationPolicy {
    return {
      id: policy.id,
      type: policy.type,
      refundTiers: policy.refundTiers || [],
      createdAt: new Date(policy.createdAt),
      updatedAt: new Date(policy.updatedAt),
      isNonRefundable: policy.refundTiers?.length === 0,
    };
  }

  private static mapProductOption(apiOption: any): ProductOption {
    return {
      id: apiOption.id,
      title: apiOption.title,
      isPickupIncluded: apiOption.isPickupIncluded,
      isDefault: apiOption.isDefault,
      optionDetals:
        apiOption.customizations?.map((customization: any) =>
          ProductMapper.mapProductOptionCustomization(customization),
        ) || [],
      startLocations:
        apiOption.startLocations?.map((startLocation: any) =>
          ProductMapper.mapMeetingPoint(
            startLocation.meetingPoint || startLocation,
          ),
        ) || [],
      seasons: ProductMapper.mapPricingRecordsToSeasons(
        apiOption.pricingRecords || [],
      ),
    };
  }

  private static mapProductOptionCustomization(
    apiCustomization: any,
  ): ProductOptionCustomization {
    return {
      type: apiCustomization.type as CustomizationTypeEnum,
      name: apiCustomization.name,
      description: apiCustomization.description,
      integerValue: apiCustomization.integerValue || 0,
    };
  }

  private static mapPricingRecordsToSeasons(
    pricingRecords: any[],
  ): ProductSeason[] {
    // Group pricing records by season
    const seasonMap = new Map<string, ProductSeason>();

    pricingRecords.forEach((record: any) => {
      if (!record.season) return;

      const seasonId = record.season.id;
      if (!seasonMap.has(seasonId)) {
        seasonMap.set(seasonId, {
          id: seasonId,
          name: record.season.name,
          startDate: new Date(record.season.startDate),
          endDate: record.season.endDate
            ? new Date(record.season.endDate)
            : undefined,
          timeModel: record.season.timeModel as PricingTimeModel,
          isDefault: record.season.isDefault || false,
          pricingRecords: [],
        });
      }

      const season = seasonMap.get(seasonId)!;
      season.pricingRecords.push(ProductMapper.mapPricingRecord(record));
    });

    return Array.from(seasonMap.values());
  }

  private static mapPricingRecord(apiRecord: any): PricingRecord {
    // Convert API time format (HH:MM) to UI format (h:mma)
    const convertTimeToUIFormat = (time: string): string => {
      const [hours, minutes] = time.split(":").map(Number);
      const period = hours >= 12 ? "pm" : "am";
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, "0")}${period}`;
    };

    // Convert API dayTimeRanges to UI format
    const convertDayTimeRanges = (
      apiRanges: any,
    ): Record<
      string,
      { start: string; end: string; hasBooking: boolean }[]
    > => {
      const converted: Record<
        string,
        { start: string; end: string; hasBooking: boolean }[]
      > = {};

      if (apiRanges) {
        Object.entries(apiRanges).forEach(([day, ranges]: [string, any]) => {
          if (Array.isArray(ranges)) {
            converted[day] = ranges.map((range: any) => ({
              start: convertTimeToUIFormat(range.start),
              end: convertTimeToUIFormat(range.end),
              hasBooking: range.hasBooking ?? false,
            }));
          }
        });
      }

      return converted;
    };
    const isAgeBandPricingMultiTier =
      !!apiRecord.pricingPackage?.ageBandPricingTiers
        ?.withTierNumParticipantRange;
    const isAgeSimpleTier =
      !!apiRecord.pricingPackage?.ageBandPricingTiers?.simpleTier;
    const record: PricingRecord = {
      id: apiRecord.id,
      daysOfWeek:
        apiRecord.daysOfWeek?.map((day: string) => day as DayOfWeekEnum) || [],
      startTimes: apiRecord.startTimes?.map(convertTimeToUIFormat) || [],
      dayTimeRanges: convertDayTimeRanges(apiRecord.dayTimeRanges),
      hasBooking: apiRecord.hasBooking ?? false,
      fixStartTimeBookingFlags: apiRecord.fixStartTimeBookingFlags || {},
      externalReference: apiRecord.externalReference,
      pricingPackage: {
        isAgeBandPricingMultiTier,
        type:
          apiRecord.pricingPackage?.type || PricingType.TRAVELLER_BY_AGE_BAND,
        ageBandPricingTiers: isAgeBandPricingMultiTier
          ? Object.entries(
              apiRecord.pricingPackage.withTierNumParticipantRange,
            ).reduce(
              (acc, [ageBand, tiers]) => ({
                ...acc,
                [ageBand]: (tiers as any[]).map((tier) => ({
                  netPriceCents: tier.netPriceCents,
                  retailPriceCents: tier.retailPriceCents,
                  minParticipants: tier.minParticipants,
                  maxParticipants: tier.maxParticipants,
                })),
              }),
              {} as AgeBandPricingTier,
            )
          : Object.entries(
              (apiRecord.pricingPackage.simpleTier as Record<
                AgeBand,
                AgeBandPricingSimpleTierDto
              >) || {},
            ).reduce(
              (acc, [ageBand, tier]) => ({
                ...acc,
                [ageBand]: [
                  {
                    netPriceCents: tier.netPriceCents,
                    retailPriceCents: tier.retailPriceCents,
                    minParticipants: tier.minParticipants,
                    maxParticipants: tier.maxParticipants,
                  },
                ],
              }),
              {} as AgeBandPricingTier,
            ),
      },
    };

    // Handle age band pricing tiers
    if (isAgeBandPricingMultiTier) {
      const ageBandPricingTiers: AgeBandPricingTier = {} as AgeBandPricingTier;

      Object.entries(
        apiRecord.pricingPackage.ageBandPricingTiers
          .withTierNumParticipantRange,
      ).forEach(([ageBand, tiers]: [string, any]) => {
        if (Array.isArray(tiers)) {
          ageBandPricingTiers[ageBand as AgeBand] = tiers.map((tier: any) => ({
            type: "AgeBand" as const,
            netPriceCents: tier.netPriceCents,
            retailPriceCents: tier.retailPriceCents,
            minParticipants: tier.minParticipants,
            maxParticipants: tier.maxParticipants,
          }));
        }
      });

      record.pricingPackage.ageBandPricingTiers = ageBandPricingTiers;
    } else if (isAgeSimpleTier) {
      record.pricingPackage.ageBandPricingTiers = Object.entries(
        (apiRecord.pricingPackage.ageBandPricingTiers?.simpleTier ||
          {}) as Record<AgeBand, AgeBandPricingSimpleTierDto>,
      ).reduce(
        (acc, [ageBand, tier]) => ({
          ...acc,
          [ageBand]: [
            {
              netPriceCents: tier.netPriceCents,
              retailPriceCents: tier.retailPriceCents,
              maxParticipants: tier.maxParticipants,
            },
          ],
        }),
        {} as AgeBandPricingTier,
      );
    } else if (apiRecord.pricingPackage?.groupPricingTier) {
      // Handle group pricing tier
      record.pricingPackage.groupPricingTier = {
        type: "Group" as const,
        netPriceCents: apiRecord.pricingPackage.groupPricingTier.netPriceCents,
        retailPriceCents:
          apiRecord.pricingPackage.groupPricingTier.retailPriceCents,
        maxParticipants:
          apiRecord.pricingPackage.groupPricingTier.maxParticipants,
      };
    }

    return record;
  }

  static mapPricingRecordToApiFormat(
    uiRecord: PricingRecord,
  ): CreatePricingRecordDto {
    // Convert UI time format (h:mma) to API format (HH:MM)
    const convertTimeToApiFormat = (time: string): string => {
      const [timePart, period] = time.split(/([ap]m)/i);
      let [hours, minutes] = timePart.split(":").map(Number);

      if (period.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (period.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }

      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    };

    const convertDayTimeRangesToApi = (
      uiRanges: Record<string, { start: string; end: string }[]>,
    ): any => {
      const converted: any = {};

      Object.entries(uiRanges).forEach(([day, ranges]) => {
        converted[day] = ranges.map((range) => ({
          start: convertTimeToApiFormat(range.start),
          end: convertTimeToApiFormat(range.end),
        }));
      });

      return converted;
    };

    const apiRecord: any = {
      id: uiRecord.id,
      daysOfWeek: uiRecord.daysOfWeek,
      startTimes: uiRecord.startTimes?.map(convertTimeToApiFormat),
      dayTimeRanges: uiRecord.dayTimeRanges
        ? convertDayTimeRangesToApi(uiRecord.dayTimeRanges)
        : undefined,
      externalReference: uiRecord.externalReference,
    };

    // Handle pricing package structure
    if (uiRecord.pricingPackage) {
      apiRecord.pricingPackage = {
        type: uiRecord.pricingPackage.type,
      };

      // Handle age band pricing tiers
      if (uiRecord.pricingPackage.ageBandPricingTiers) {
        const pricingPackageAgeBand =
          uiRecord.pricingPackage.ageBandPricingTiers;
        const ageBandPricingTiers: any = match(
          uiRecord.pricingPackage.isAgeBandPricingMultiTier,
        )
          .with(true, () => ({
            withTierNumParticipantRange: Object.entries(
              pricingPackageAgeBand,
            ).reduce(
              (acc, [ageBand, tiers]) =>
                tiers?.length > 0
                  ? {
                      ...acc,
                      [ageBand]: tiers.map((tier) => ({
                        netPriceCents: tier.netPriceCents,
                        retailPriceCents: tier.retailPriceCents,
                        minParticipants: tier.minParticipants,
                        maxParticipants: tier.maxParticipants,
                      })),
                    }
                  : acc,
              {},
            ),
          }))
          .otherwise(() => ({
            simpleTier: Object.entries(pricingPackageAgeBand).reduce(
              (acc, [ageBand, tiers]) => ({
                ...acc,
                [ageBand]: {
                  netPriceCents: tiers[0].netPriceCents,
                  retailPriceCents: tiers[0].retailPriceCents,
                  maxParticipants: tiers[0].maxParticipants,
                },
              }),
              {},
            ),
          }));

        apiRecord.pricingPackage.ageBandPricingTiers = ageBandPricingTiers;
      }

      // Handle group pricing tier
      if (uiRecord.pricingPackage.groupPricingTier) {
        apiRecord.pricingPackage.groupPricingTier = {
          // type: uiRecord.pricingPackage.groupPricingTier.type,
          netPriceCents: uiRecord.pricingPackage.groupPricingTier.netPriceCents,
          retailPriceCents:
            uiRecord.pricingPackage.groupPricingTier.retailPriceCents,
          maxParticipants:
            uiRecord.pricingPackage.groupPricingTier.maxParticipants,
        };
      }
    }

    return apiRecord;
  }

  static mapPricingPackageToApiFormat(uiPackage: PricingPackage): any {
    if (uiPackage.type === PricingType.GROUP_PRICING) {
      return {
        type: PricingType.GROUP_PRICING,
        // group: uiPackage.group,
      };
    } else if (uiPackage.type === PricingType.TRAVELLER_BY_AGE_BAND) {
      return {
        type: PricingType.TRAVELLER_BY_AGE_BAND,
        // ageDefinitions: uiPackage.ageDefinitions,
      };
    }

    return uiPackage;
  }

  static mapProductSeasonToApiFormat(uiSeason: ProductSeason): any {
    return {
      id: uiSeason.id,
      name: uiSeason.name,
      startDate: uiSeason.startDate,
      endDate: uiSeason.endDate,
      timeModel: uiSeason.timeModel,
      isDefault: uiSeason.isDefault,
      pricingRecords: uiSeason.pricingRecords?.map((record) =>
        ProductMapper.mapPricingRecordToApiFormat(record),
      ),
    };
  }

  static mapProductOptionToApiFormat(uiOption: ProductOption): any {
    return {
      id: uiOption.id,
      title: uiOption.title,
      isPickupIncluded: uiOption.isPickupIncluded,
      isDefault: uiOption.isDefault,
      customizations: uiOption.optionDetals?.map((detail) => ({
        type: detail.type,
        name: detail.name,
        description: detail.description,
        integerValue: detail.integerValue,
      })),
      startLocations: uiOption.startLocations?.map((location) => ({
        meetingPoint: location,
      })),
      seasons: uiOption.seasons?.map((season) =>
        ProductMapper.mapProductSeasonToApiFormat(season),
      ),
    };
  }
}
